import "quill/dist/quill.snow.css";

import { zodResolver } from "@hookform/resolvers/zod";
import { ExpandMoreSharp, FileCopySharp, SaveSharp } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Button,
  Card,
  CardActions,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Delta } from "quill";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuill } from "react-quilljs";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { useSnackbar } from "../../contexts/SnackbarContext";
import useGlobal from "../../hooks/useGlobal";
import useTag from "../../hooks/useTag";
import { getECPs } from "../../services/ecp.service";
import {
  createTask,
  createUrgentTask,
  updateTask,
} from "../../services/task.service";
import { uploadImage, viewImage } from "../../services/upload.service";
import { getOnlineUsers } from "../../services/user.service";
import { getUsers } from "../../services/user.service";
import { ECP } from "../../types/ecp";
import { TagOptions } from "../../types/global";
import { Tag } from "../../types/tag";
import { TaskForm } from "../../types/task";
import { User, UserData, UserOnline, UserOnlineData } from "../../types/user";
import {
  channelList,
  getTaskTypeColor,
  orderTypeList,
  taskTypeList,
} from "../../utils/constants";
import CustomChip from "../CustomChip";
import CustomConfirmDialog from "../CustomConfirmDialog";
import CustomDialog from "../CustomDialog";
import ShowECPTags from "../ShowECPTags";
import Upload from "../Upload";

type PropsType = {
  formData: TaskForm;
  taskType?: "YOUR_TASK";
  page?: string | null;
};

const disabledFontColor = {
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#25272c",
    WebkitTextFillColor: "#25272c",
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#25272c",
  },
};

const Form = (props: PropsType) => {
  const { formData, taskType } = props;
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { getOrderCycleOptions } = useGlobal();
  const { data: tagData } = useTag(true, null, {
    is_active: true,
  });

  const quillTheme = "snow";

  const modules = {
    toolbar: false,
    clipboard: { matchVisual: false },
  };

  const placeholder = "Copy and paste an image or drag and drop it here";

  const formats = ["image"];

  const { quill, quillRef } = useQuill({
    theme: quillTheme,
    modules,
    formats,
    placeholder,
  });

  const formSchema = z
    .object({
      ecp_id: z.string().min(1, {
        message: "ECP code is required or not found.",
      }),
      ecp_code: z.string().min(1, {
        message: "ECP code is required.",
      }),
      ecp_id_ship_to: z.string().min(1, {
        message: "Ship to ECP code is required or not found.",
      }),
      ecp_ship_to_code: z.string().min(1, {
        message: "Ship to ECP code is required.",
      }),
      order_type: z.string().min(1, {
        message: "Order type is required. Please select a order type.",
      }),
      order_cycle: z.string().min(1, {
        message: "Order cycle is required. Please select a order cycle.",
      }),
      task_type: z.string().min(1, {
        message: "Task type is required. Please select a task type.",
      }),
      tag_names: z.array(z.string()).optional(),
      order_quantity: z.number(),
      tracking_numbers: z.string().optional(),
      to_user_id: z.string().optional(),
      urgent_note: z.string().min(1, "Please enter a urgent note.").optional(),
      content: z.string().optional(),
      content_images: z.array(z.string()).optional(),
      more_images: z.array(z.string()).optional(),
      create_channel: z.string().min(1, {
        message: "Create channel is required. Please select a create channel.",
      }),
    })
    .superRefine((data, ctx) => {
      if (taskType === "YOUR_TASK") {
        if (!data.urgent_note) {
          ctx.addIssue({
            path: ["urgent_note"],
            message: "Please enter a urgent note.",
            code: z.ZodIssueCode.custom,
          });
        }
      }
    });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openSaveMenu = Boolean(anchorEl);
  const [openMultiOrder, setOpenMultiOrder] = useState(false);
  const [saveData, setSaveData] = useState<z.infer<typeof formSchema> | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [openSaveAndCopyDialog, setOpenSaveAndCopyDialog] = useState(false);
  const [ecp, setECP] = useState<ECP | null>(null);
  const [ecpLoading, setECPLoading] = useState(false);
  const [shipECP, setShipECP] = useState<ECP | null>(null);
  const [shipToLoading, setShipToLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState<TagOptions[]>([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [openSaveUrgentTask, setOpenSaveUrgentTask] = useState(false);
  const [captureImages, setCaptureImages] = useState<string[]>([]);
  const [moreImages, setMoreImages] = useState<string[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<User[] | null>(null);
  const [onlineUsersLoading, setOnlineUsersLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: formData,
  });

  const moveCursorToLastLine = () => {
    if (!quill) return;

    setTimeout(() => {
      const length = quill.getLength();
      quill.setSelection(length, 0); // Set cursor to the last position
    }, 0); // Delay the execution until the next event loop
  };

  const extractImageUrlsFromHtml = (htmlContent: string) => {
    const regex = /<img[^>]+src="([^">]+)"/g;
    const imageNames = [];
    let match;

    while ((match = regex.exec(htmlContent))) {
      const imgSrc = match[1];
      const imgName = imgSrc.split("/").pop();
      if (imgName) {
        imageNames.push(imgName);
      }
    }

    return imageNames;
  };

  const removeBase64Images = () => {
    if (!quill) return;
    const editorContent = quill.root.innerHTML;
    const updatedContent = editorContent.replace(
      /<img[^>]+src="data:image\/[^">]+"[^>]*>/g,
      "",
    );
    quill.root.innerHTML = updatedContent;
  };

  const uploadFile = async (file: File): Promise<string> => {
    const fileExtension = file.name.split(".").pop() || "jpeg";

    const formData = new FormData();
    formData.append("file", file);
    formData.append("file-name", `${uuidv4()}.${fileExtension}`);

    const response = await uploadImage(formData);
    if (response) {
      let items = captureImages;
      if (items) {
        items.push(response?.fileName);
        setCaptureImages(items);
        const imageUrl = viewFile(response?.fileName);
        if (imageUrl) {
          return imageUrl;
        }
      }
    }
    return "";
  };

  const viewFile = (fileName: string) => {
    const response = viewImage(fileName);
    if (response) {
      return response;
    }
  };

  const insertIntoEditor = (imageUrl: string) => {
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertEmbed(range?.index || 0, "image", imageUrl);
      }
    }
  };

  const fetchOnlineUsers = useCallback(async () => {
    setOnlineUsersLoading(true);
    const response = await getOnlineUsers();
    let isOnlineUsers: UserOnline[] = [];
    response?.online_users?.forEach((user: UserOnline) => {
      isOnlineUsers.push(user);
    });
    const responseUsers = await getUsers({
      page_number: 1,
      page_size: 1000,
      is_active: true,
      sort: "name|asc",
    });
    let users: User[] = [];
    responseUsers?.users?.forEach((user: User) => {
      if (user.duties.includes("keyin")) {
        users.push(user);
      }
    });
    // Merge online users with all users if same id exists use online user then sort online users first
    const mergedUsers = users.map((user) => {
      const onlineUser = isOnlineUsers.find(
        (online) => online.user_id === user.id,
      );
      return onlineUser
        ? { ...user, is_online: true }
        : { ...user, is_online: false };
    });
    const result: User[] = mergedUsers.sort((a, b) => {
      if (a.is_online && !b.is_online) return -1; // a is online, b is offline
      if (!a.is_online && b.is_online) return 1; // a is offline, b is online
      return a.name.localeCompare(b.name); // both are online or both are offline, sort by name
    });

    setOnlineUsers(result);
    setOnlineUsersLoading(false);
  }, []);

  useEffect(() => {
    if (taskType === "YOUR_TASK") {
      fetchOnlineUsers();
    }
  }, [taskType]);

  useEffect(() => {
    if (!quill) return;

    quill.clipboard.addMatcher("img", () => new Delta());

    quill.root.addEventListener("paste", async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      event.preventDefault();

      for (let item of items) {
        if (item.type.startsWith("image")) {
          const file = item.getAsFile();
          if (file) {
            const imageUrl = await uploadFile(file);
            if (imageUrl) {
              setTimeout(() => {
                removeBase64Images();
                moveCursorToLastLine();
              }, 0);
              insertIntoEditor(imageUrl);
            }
          }
        }
      }
    });

    quill.on("text-change", () => {
      const html = quill.root.innerHTML;

      setValue("content", html);

      const imageUrlsInEditor = extractImageUrlsFromHtml(html);
      const remainingImages = captureImages.filter((imgUrl) =>
        imageUrlsInEditor.includes(imgUrl),
      );

      setValue("content_images", remainingImages);
    });
  }, [quill]);

  useEffect(() => {
    return () => {
      setAnchorEl(null);
      setOpenMultiOrder(false);
      setSaveData(null);
      setOpenSaveAndCopyDialog(false);
      setECP(null);
      setMoreImages([]);
      setOpenAlert(false);
    };
  }, []);

  useEffect(() => {
    if (tagData && tagData?.length > 0) {
      const items: TagOptions[] = [];

      tagData.forEach((tag: Tag) => {
        items.push({
          label: tag.name,
          value: tag.name,
          text_color_code: tag.text_color_code,
          background_color_code: tag.background_color_code,
        });
      });

      setTagOptions(items);
    }
  }, [tagData]);

  useEffect(() => {
    if (formData) {
      reset(formData);
      if (formData?.ecp_code) fetchECPs(formData?.ecp_code);
      if (formData?.ecp_ship_to_code)
        fetchShipToCode(formData?.ecp_ship_to_code);
      if (quill) {
        quill.root.innerHTML = formData?.content || "";
      }
      setCaptureImages(formData?.content_images || []);
      setMoreImages(formData?.more_images || []);
    }
  }, [formData, reset, quill]);

  const fetchECPs = useCallback(async (ecpCode: string) => {
    setECPLoading(true);
    try {
      const response = await getECPs({
        page_number: 1,
        page_size: 10,
        is_active: true,
        code: ecpCode.toUpperCase(),
      });
      if (response?.total_record === 0) {
        setValue("ecp_id", "");
        setECP(null);
        throw new Error("Not found ECP Code");
      } else if (response?.total_record === 1) {
        setValue("ecp_id", response?.ecps[0]?.id);
        setECP(response?.ecps[0]);
        if (response?.ecps[0]?.alert_1 && response?.ecps[0]?.alert_1 !== "") {
          setOpenAlert(true);
        }
      } else {
        setValue("ecp_id", "");
        setECP(null);
        throw new Error("More than one ECP found in the response.");
      }
    } catch (error) {
      console.error("🚀 ~ fetchECPs ~ error:", error);
      showSnackbar((error as Error)?.message ?? "Cannot get ECP", "error");
    } finally {
      setECPLoading(false);
    }
  }, []);

  const fetchShipToCode = useCallback(async (ecpCode: string) => {
    setShipToLoading(true);
    try {
      const response = await getECPs({
        page_number: 1,
        page_size: 10,
        is_active: true,
        code: ecpCode.toUpperCase(),
      });
      if (response?.total_record === 0) {
        setValue("ecp_id_ship_to", "");
        setShipECP(null);
        throw new Error("Not found ECP Code");
      } else if (response?.total_record === 1) {
        setValue("ecp_id_ship_to", response?.ecps[0]?.id);
        setShipECP(response?.ecps[0]);
      } else {
        setValue("ecp_id_ship_to", "");
        setShipECP(null);
        throw new Error("More than one ECP found in the response.");
      }
    } catch (error) {
      console.error("🚀 ~ fetchECPs ~ error:", error);
      showSnackbar((error as Error)?.message ?? "Cannot get ECP", "error");
    } finally {
      setShipToLoading(false);
    }
  }, []);

  const saveChangeTask = async (values: z.infer<typeof formSchema>) => {
    if (formData?.id) {
      setIsLoading(true);
      const body = {
        ecp_id: values.ecp_id,
        ecp_id_ship_to: values.ecp_id_ship_to,
        order_type: values.order_type,
        order_cycle: values.order_cycle,
        task_type: values.task_type,
        tag_names: values.tag_names,
        order_quantity: Number(values.order_quantity) || 1,
        content: values.content,
        content_images: values.content_images,
        more_images: values.more_images,
      };

      try {
        const response = await updateTask(formData.id, body);

        if (response && response?.id) {
          showSnackbar(
            `Save Changes Task - ${response?.ecp?.code} - Successfully`,
            "success",
          );
          navigate("/user/task-creator");
        }
      } catch (error) {
        console.error("🚀 ~ onSubmit ~ error:", error);
        showSnackbar(`Save Changes Task Failed`, "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onSubmit = async (
    saveType: "UPDATE" | "SAVE_COPY" | "SAVE_URGENT_TASK" | "SAVE" = "SAVE",
    values: z.infer<typeof formSchema>,
  ) => {
    setSaveData(values);

    switch (saveType) {
      case "UPDATE":
        await saveChangeTask(values);
        break;
      case "SAVE_COPY":
        setOpenSaveAndCopyDialog(true);
        break;
      case "SAVE_URGENT_TASK":
        setOpenSaveUrgentTask(true);
        break;
      default:
        setOpenMultiOrder(true);
    }
  };

  const handleClickSave = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSave = () => {
    setAnchorEl(null);
  };

  const handleDiscardMultiOrder = () => {
    handleCloseSave();
    setOpenMultiOrder(false);
  };

  const handleCloseSaveAndCopyDialog = () => {
    handleCloseSave();
    setOpenSaveAndCopyDialog(false);
  };

  const handleCloseSaveUrgentTask = () => {
    setOpenSaveUrgentTask(false);
  };

  const onSaveAndExit = async (
    saveType: "UPDATE" | "SAVE_COPY" | "SAVE_URGENT_TASK" | "SAVE" = "SAVE",
  ) => {
    setIsLoading(true);
    try {
      if (saveData) {
        const body = {
          ecp_id: saveData?.ecp_id,
          ecp_id_ship_to: saveData?.ecp_id_ship_to,
          order_type: saveData?.order_type,
          order_cycle: saveData?.order_cycle,
          task_type: saveData?.task_type,
          tag_names: saveData?.tag_names,
          order_quantity: Number(saveData?.order_quantity) || 1,
          content: saveData?.content,
          content_images: saveData?.content_images,
          more_images: saveData?.more_images,
        };

        const response = await createTask(body);

        if (response && response?.id) {
          showSnackbar(
            `Create Task - ${response?.ecp?.code} - Successfully`,
            "success",
          );

          if (saveType === "SAVE_COPY") {
            if (quill) {
              quill.root.innerHTML = "";
            }
            setValue("content", "");
            setValue("content_images", []);
            setValue("more_images", []);
            setMoreImages([]);
            return;
          }
          navigate("/user/task-creator");
        }
      }
    } catch (error) {
      console.error("🚀 ~ onSaveAndExit ~ error:", error);
      showSnackbar(`Create Task Failed`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmSaveAndCopy = async () => {
    await onSaveAndExit("SAVE_COPY");
    handleCloseSaveAndCopyDialog();
  };

  const onSaveUrgentTask = async () => {
    if (!saveData?.to_user_id) {
      showSnackbar(`Please select user for your task`, "error");
      return;
    }

    setIsLoading(true);
    try {
      if (saveData) {
        const body = {
          ecp_id: saveData?.ecp_id,
          ecp_id_ship_to: saveData?.ecp_id_ship_to,
          order_type: saveData?.order_type,
          order_cycle: saveData?.order_cycle,
          task_type: saveData?.task_type,
          tag_names: saveData?.tag_names,
          order_quantity: Number(saveData?.order_quantity) || 1,
          to_user_id: saveData?.to_user_id,
          urgent_note: saveData?.urgent_note,
          content: saveData?.content,
          content_images: saveData?.content_images,
          more_images: saveData?.more_images,
        };

        let response = null;

        if (formData?.id) {
          response = await updateTask(formData.id, body);

          if (response && response?.id) {
            showSnackbar(
              `Save Changes Task - ${response?.ecp?.code} - Successfully`,
              "success",
            );
            navigate(`/user/${props.page}/your-tasks`);
          }
        } else {
          response = await createUrgentTask(body);
          if (response && response?.id) {
            showSnackbar(
              `Create Urgent Task - ${response?.ecp?.code} - Successfully`,
              "success",
            );
            navigate(`/user/${props.page}/your-tasks`);
          }
        }
      }
    } catch (error) {
      console.error("🚀 ~ onSaveUrgentTask ~ error:", error);
      showSnackbar(`Create Urgent Task Failed`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmSaveUrgentTask = async () => {
    await onSaveUrgentTask();
    handleCloseSaveUrgentTask();
  };

  const handleSelectTags = (tag: { label: string; value: string }[]) => {
    const updateTag = tag.map((t) => t.label);
    setValue("tag_names", updateTag);
  };

  const debounceRef = useRef<any>(null);

  const onSearch = (value: string, isShipTo = false) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (isShipTo) {
      setShipToLoading(true);
    } else {
      setECPLoading(true);
    }

    debounceRef.current = setTimeout(async () => {
      isShipTo ? await fetchShipToCode(value) : await fetchECPs(value);

      if (isShipTo) {
        setShipToLoading(false);
      } else {
        setECPLoading(false);
      }
    }, 1000);
  };

  const onDiscard = () => {
    // if (taskType === "YOUR_TASK") {
    //   navigate(`/user/${props.page}/your-tasks`);
    // } else {
    //   navigate("/user/task-creator");
    // }
    navigate(-1);
  };

  return (
    <>
      <Card className="p-6" component="form">
        <Typography variant="body1" className="font-bold mb-4">
          Task Info
        </Typography>
        <div className="grid container grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          <div className="col-span-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="ecp_code" required>
                  ECP Code Sold To
                </InputLabel>
                <Controller
                  name="ecp_code"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      placeholder="ECP Code"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(e);
                        onSearch((e.target as HTMLInputElement).value);
                      }}
                      value={value}
                      inputRef={ref}
                      disabled={
                        taskType === "YOUR_TASK" && formData?.is_reject_task
                      }
                    />
                  )}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="name">
                  ECP Name Sold To{" "}
                  {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <TextField
                  placeholder="ECP Name"
                  fullWidth
                  value={ecp?.name || ""}
                  disabled
                  error={!!errors?.ecp_id}
                  helperText={errors?.ecp_id?.message}
                  sx={disabledFontColor}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="ecp_ship_to_code" required>
                  ECP Code Ship To
                </InputLabel>
                <Controller
                  name="ecp_ship_to_code"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      placeholder="ECP Code Ship To"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(e);
                        onSearch((e.target as HTMLInputElement).value, true);
                      }}
                      value={value}
                      inputRef={ref}
                    />
                  )}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="name_ship_to">
                  ECP Name Ship To
                  {shipToLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <TextField
                  placeholder="ECP Name Ship To"
                  fullWidth
                  value={shipECP?.name || ""}
                  disabled
                  error={!!errors?.ecp_id_ship_to}
                  helperText={errors?.ecp_id_ship_to?.message}
                  sx={disabledFontColor}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="order_type" required>
                  Order Type
                </InputLabel>
                <Controller
                  name="order_type"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl error={!!error}>
                      <Select
                        displayEmpty
                        onChange={onChange}
                        value={value}
                        inputRef={ref}
                        renderValue={(selected) => {
                          if (selected === "") {
                            return (
                              <div className="text-text-disabled">
                                Order Type
                              </div>
                            );
                          }

                          const selectedType = orderTypeList.find(
                            (ot) => ot.value === selected,
                          );

                          if (selectedType) {
                            return selectedType.label;
                          }

                          return null;
                        }}
                      >
                        {orderTypeList?.map((ot, index) => (
                          <MenuItem
                            value={ot.value}
                            key={`order-type-${index}`}
                          >
                            {ot.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="order_cycle" required>
                  Order Cycle
                </InputLabel>
                <Controller
                  name="order_cycle"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl error={!!error}>
                      <Select
                        ref={ref}
                        displayEmpty
                        value={
                          getOrderCycleOptions()?.some(
                            (tag: any) => tag.value === value,
                          )
                            ? value
                            : ""
                        }
                        onChange={onChange}
                        renderValue={(selected) => {
                          if (!getOrderCycleOptions()) return null;

                          if (selected === "") {
                            return (
                              <div className="text-text-disabled">
                                Order Cycle
                              </div>
                            );
                          }

                          const selectedTag = getOrderCycleOptions().find(
                            (tag: any) => tag.value === selected,
                          );

                          if (selectedTag) {
                            return <>{selectedTag.chip}</>;
                          }

                          return null;
                        }}
                      >
                        {getOrderCycleOptions()?.map(
                          (tag: any, index: number) => (
                            <MenuItem
                              key={`payment-${index}`}
                              value={tag.value}
                            >
                              {tag.chip}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="task_type" required>
                  Task Type
                </InputLabel>
                <Controller
                  name="task_type"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl error={!!error}>
                      <Select
                        displayEmpty
                        onChange={onChange}
                        value={value}
                        inputRef={ref}
                        renderValue={(selected) => {
                          if (selected === "") {
                            return (
                              <div className="text-text-disabled">
                                Task Type
                              </div>
                            );
                          }

                          const selectedType = taskTypeList.find(
                            (tt) => tt.value === selected,
                          );

                          if (selectedType) {
                            return (
                              <span
                                style={{
                                  color: getTaskTypeColor(selectedType.value),
                                  fontWeight: 600,
                                }}
                              >
                                {selectedType.label}
                              </span>
                            );
                          }

                          return null;
                        }}
                      >
                        {taskTypeList?.map((tt, index) => (
                          <MenuItem value={tt.value} key={`task-type-${index}`}>
                            <span
                              style={{
                                color: getTaskTypeColor(tt.value),
                                fontWeight: 600,
                              }}
                            >
                              {tt.label}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className="col-span-1 flex flex-col gap-3">
                <InputLabel htmlFor="create_channel">
                  Channel
                  {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <Controller
                  name="create_channel"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl error={!!error}>
                      <Select
                        displayEmpty
                        onChange={onChange}
                        value={value}
                        inputRef={ref}
                        disabled
                        renderValue={(selected) => {
                          if (selected === "") {
                            return (
                              <div className="text-text-disabled">
                                Create Channel
                              </div>
                            );
                          }

                          const selectedChannel = channelList.find(
                            (tt) => tt.value === selected,
                          );

                          if (selectedChannel) {
                            return selectedChannel.label;
                          }

                          return null;
                        }}
                      >
                        {channelList?.map((tt, index) => (
                          <MenuItem value={tt.value} key={`channel-${index}`}>
                            {tt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="ecp_promotion_detail">
                  ECP Promotion Detail{" "}
                  {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <TextField
                  placeholder="ECP Promotion Detail"
                  fullWidth
                  value={ecp?.detail || ""}
                  disabled
                  error={!!errors?.ecp_id}
                  helperText={errors?.ecp_id?.message}
                  sx={disabledFontColor}
                  multiline
                  rows={4}
                  maxRows={8}
                />
              </div>
              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="ecp_logistic_note">
                  ECP Logistic Note{" "}
                  {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <TextField
                  placeholder="ECP Logistic Note"
                  fullWidth
                  value={ecp?.note || ""}
                  disabled
                  error={!!errors?.ecp_id}
                  helperText={errors?.ecp_id?.message}
                  sx={disabledFontColor}
                />
              </div>
              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="ecp_alert">
                  ECP Alert {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <TextField
                  placeholder="ECP Alert"
                  fullWidth
                  value={ecp?.alert_1 || ""}
                  disabled
                  sx={disabledFontColor}
                />
              </div>
              {(taskType === "YOUR_TASK" ||
                (taskType !== "YOUR_TASK" && formData?.id)) && (
                <>
                  <div className="col-span-1 flex flex-col gap-3">
                    <InputLabel htmlFor="order_quantity">Order QTY</InputLabel>
                    <Controller
                      name="order_quantity"
                      control={control}
                      render={({
                        field: { ref, onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          placeholder="Order Quantity"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                          onChange={(e) => {
                            let inputValue = e.target.value;

                            if (
                              inputValue === "" ||
                              /^[0-9]+$/.test(inputValue)
                            ) {
                              setValue(
                                "order_quantity",
                                inputValue === "" ? 0 : Number(inputValue),
                              );
                              setSaveData((prev: any) => ({
                                ...prev,
                                order_quantity: inputValue,
                              }));
                            }
                          }}
                          value={value}
                          inputRef={ref}
                          slotProps={{
                            htmlInput: {
                              inputMode: "numeric",
                              pattern: "[0-9]*",
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-1 hidden md:flex md:flex-col md:gap-3"></div>
                </>
              )}
              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="tracking_numbers">Tracking No.</InputLabel>
                <Controller
                  name="tracking_numbers"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      disabled
                      placeholder="Tracking No"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      onChange={onChange}
                      value={value || ""}
                      inputRef={ref}
                      sx={disabledFontColor}
                    />
                  )}
                />
              </div>

              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="ecp_tag">
                  ECP Tag {ecpLoading && <CircularProgress size="0.85rem" />}
                </InputLabel>
                <ShowECPTags ecp={ecp} />
              </div>
              <div className="col-span-full flex flex-col gap-3">
                <InputLabel htmlFor="tag_names">Tag Add</InputLabel>
                <Controller
                  name="tag_names"
                  control={control}
                  render={({
                    field: { ref, onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl error={!!error}>
                      <Autocomplete
                        multiple
                        limitTags={2}
                        id="checkboxes-tags-demo"
                        options={tagOptions}
                        disableCloseOnSelect
                        ref={ref}
                        value={tagOptions.filter((u) => {
                          if (value && value.includes(u.label)) {
                            return u;
                          }
                        })}
                        // defaultValue={[usageOptions.find((o) => o.value === "payment")]}
                        onChange={(_, newValue) => handleSelectTags(newValue)}
                        getOptionLabel={(option) => option.label}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={key}
                                label={option.label}
                                size="small"
                                {...tagProps}
                                sx={{
                                  backgroundColor: option.background_color_code,
                                  "& .MuiChip-label": {
                                    color: option.text_color_code,
                                  },
                                }}
                              />
                            );
                          })
                        }
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          return (
                            <li key={key} {...optionProps}>
                              <Checkbox
                                icon={
                                  <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              <CustomChip
                                label={option.label}
                                textColor={option.text_color_code}
                                bgColor={option.background_color_code}
                              />
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Tag Add" />
                        )}
                      />
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              {(taskType === "YOUR_TASK" ||
                formData?.is_urgent_task === true) && (
                <>
                  <div className="col-span-full flex flex-col gap-3">
                    <InputLabel htmlFor="urgent_note" required>
                      Urgent Note
                    </InputLabel>
                    <Controller
                      name="urgent_note"
                      control={control}
                      render={({
                        field: { ref, onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          placeholder="Urgent Note"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                          onChange={onChange}
                          value={value}
                          inputRef={ref}
                        />
                      )}
                    />
                  </div>
                  {formData?.key_in_called_name && (
                    <div className="col-span-full flex flex-col gap-3">
                      <InputLabel htmlFor="key_in_called_name">
                        Urgent Assign
                      </InputLabel>
                      <TextField
                        placeholder="Urgent Assign"
                        fullWidth
                        value={formData?.key_in_called_name}
                        disabled
                      />
                    </div>
                  )}
                </>
              )}
              <div className="col-span-full">
                <Upload
                  files={moreImages}
                  setFiles={(files) => {
                    setMoreImages(files);
                    setValue("more_images", files);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="col-span-1">
            <div className="flex flex-col gap-3 h-full">
              <InputLabel htmlFor="capture">Paste Capture</InputLabel>
              <div
                style={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <div ref={quillRef} style={{ borderRadius: "4px" }} />
              </div>
            </div>
          </div>
        </div>

        {openMultiOrder && (
          <CustomDialog
            open={openMultiOrder}
            onClose={() => setOpenMultiOrder(false)}
            title={"Multi Order"}
            content={
              <>
                <Stack useFlexGap spacing={1} mb={4}>
                  <InputLabel htmlFor="order_quantity" required>
                    Please input multi order
                  </InputLabel>
                  <Controller
                    name="order_quantity"
                    control={control}
                    render={({
                      field: { ref, onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        placeholder="Order Quantity"
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                        onChange={(e) => {
                          let inputValue = e.target.value;

                          if (
                            inputValue === "" ||
                            /^[0-9]+$/.test(inputValue)
                          ) {
                            setValue(
                              "order_quantity",
                              inputValue === "" ? 0 : Number(inputValue),
                            );
                            setSaveData((prev: any) => ({
                              ...prev,
                              order_quantity: inputValue,
                            }));
                          }
                        }}
                        value={value}
                        inputRef={ref}
                        slotProps={{
                          htmlInput: {
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent={"end"}
                  useFlexGap
                  spacing={2}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleDiscardMultiOrder()}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => onSaveAndExit()}
                    loading={isLoading}
                  >
                    Save
                  </Button>
                </Stack>
              </>
            }
          />
        )}

        {openSaveAndCopyDialog && (
          <CustomConfirmDialog
            open={openSaveAndCopyDialog}
            onClose={handleCloseSaveAndCopyDialog}
            title={`Confirm Save and Copy?`}
            content={
              <>
                <Typography variant="body2" color="grey.400">
                  This entry will be saved, and a duplicate
                </Typography>
                <Typography variant="body2" color="grey.400">
                  will remain for further editing.
                </Typography>
              </>
            }
            action={
              <>
                <Button
                  variant="outlined"
                  onClick={handleCloseSaveAndCopyDialog}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => onConfirmSaveAndCopy()}
                  autoFocus
                  loading={isLoading}
                >
                  Confirm
                </Button>
              </>
            }
          />
        )}

        {openSaveUrgentTask && (
          <CustomConfirmDialog
            open={openSaveUrgentTask}
            onClose={handleCloseSaveUrgentTask}
            title={`Urgent Task`}
            content={
              <>
                <Stack useFlexGap spacing={1} mb={4}>
                  <InputLabel htmlFor="to_user_id" required>
                    Please select user for task
                  </InputLabel>
                  <Controller
                    name="to_user_id"
                    control={control}
                    render={({
                      field: { ref, onChange, value },
                      fieldState: { error },
                    }) => (
                      <FormControl error={!!error}>
                        <Autocomplete
                          id="select-users"
                          options={onlineUsers || []}
                          ref={ref}
                          onChange={(_event, newValue) => {
                            const userId =
                              newValue &&
                              typeof newValue === "object" &&
                              "id" in newValue
                                ? newValue.id
                                : "";
                            setValue("to_user_id", userId);
                            setSaveData((prev: any) => ({
                              ...prev,
                              to_user_id: userId,
                            }));
                            onChange(userId);
                          }}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                              <li key={key} {...optionProps}>
                                {option.name}&nbsp;&nbsp;
                                {option.is_online && (
                                  <Chip
                                    key={key}
                                    color="success"
                                    label="ONLINE"
                                    size="small"
                                  />
                                )}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Users"
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {onlineUsersLoading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                            />
                          )}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Stack>
              </>
            }
            action={
              <>
                <Button variant="outlined" onClick={handleCloseSaveUrgentTask}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => onConfirmSaveUrgentTask()}
                  autoFocus
                  loading={isLoading}
                >
                  Confirm
                </Button>
              </>
            }
          />
        )}

        <CardActions className="flex justify-center md:justify-end pb-0">
          <Button variant="outlined" onClick={() => onDiscard()}>
            Discard
          </Button>
          {taskType === "YOUR_TASK" ? (
            <>
              <Button
                type="button"
                variant="contained"
                color="error"
                loading={isLoading}
                onClick={handleSubmit((values) =>
                  onSubmit("SAVE_URGENT_TASK", values),
                )}
              >
                Save Urgent Task
              </Button>
            </>
          ) : (
            <>
              {formData?.id ? (
                <Button
                  type="button"
                  variant="contained"
                  loading={isLoading}
                  onClick={handleSubmit((values) => onSubmit("UPDATE", values))}
                >
                  Save
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleClickSave}
                  >
                    Save
                    <ExpandMoreSharp className="w-6 h-6" />
                  </Button>
                  <Menu
                    id="demo-positioned-menu"
                    aria-labelledby="demo-positioned-button"
                    anchorEl={anchorEl}
                    open={openSaveMenu}
                    onClose={handleCloseSave}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{
                      mt: 1,
                    }}
                  >
                    <MenuItem
                      onClick={handleSubmit((values) =>
                        onSubmit("SAVE", values),
                      )}
                    >
                      <ListItemIcon>
                        <SaveSharp fontSize="small" />
                      </ListItemIcon>
                      Save and Exit
                    </MenuItem>
                    <MenuItem
                      onClick={handleSubmit((values) =>
                        onSubmit("SAVE_COPY", values),
                      )}
                    >
                      <ListItemIcon>
                        <FileCopySharp fontSize="small" />
                      </ListItemIcon>
                      Save and Copy
                    </MenuItem>
                  </Menu>
                </>
              )}
            </>
          )}
        </CardActions>
      </Card>

      {openAlert && (
        <CustomDialog
          open={openAlert}
          onClose={() => setOpenAlert(false)}
          title={"Customer Alert"}
          titleStyle={{
            color: "red",
          }}
          content={
            <>
              <Typography variant="body2" color="black">
                {ecp?.alert_1}
              </Typography>
            </>
          }
        />
      )}
    </>
  );
};

export default Form;
